import { Logo } from '@/components/logo';
import { Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { MailboxSetup } from '@/components/mailbox-setup';
import { useAuthStore } from '@/stores/auth';

const OAUTH_ERRORS: Record<string, string> = {
  access_denied: 'You cancelled Google sign-in.',
  no_refresh_token:
    'Google didn’t return a refresh token. Remove B-Mail under your Google Account → Security → Third-party access, then connect again.',
  token_exchange_failed: 'Could not complete Google sign-in. Please try again.'
};

export function OnboardingRoute() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const params = new URLSearchParams(window.location.search);
  const oauthSuccess = params.get('oauth_success') === '1';
  const oauthError = params.get('oauth_error');
  const [error] = useState<string | null>(
    oauthError ? OAUTH_ERRORS[oauthError] || `Google sign-in failed (${oauthError}).` : null
  );

  // On a successful Gmail link, do a full navigation into the inbox so the new
  // mailbox and its freshly-synced mail load from scratch (avoids stale state).
  useEffect(() => {
    if (oauthSuccess) window.location.replace('/inbox');
  }, [oauthSuccess]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (oauthSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bmail-navy px-4">
        <div className="text-bmail-text">Connecting your mailbox…</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bmail-navy px-4 py-8">
      <div className="w-full max-w-xl rounded-2xl bg-bmail-surface p-6 shadow-xl border border-bmail-border md:p-8">
        {/* Logo on mobile */}
        <div className="mb-4 flex justify-center md:hidden">
          <Logo className="h-24 w-auto" />
        </div>
        <h1 className="mb-2 text-xl font-bold text-bmail-text md:text-2xl">Add your first mailbox</h1>
        <p className="mb-6 text-sm text-bmail-text md:text-base">
          B-Mail needs access to your existing email account. Your credentials are encrypted and never logged.
        </p>
        {error && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400"
          >
            {error}
          </div>
        )}
        <MailboxSetup onComplete={() => navigate('/inbox')} />
      </div>
    </div>
  );
}
